import { Injectable } from '@angular/core';

import { environment } from '../../environments/environment';
import * as firebase from 'firebase/app';

import { AngularFireAuth } from '@angular/fire/auth';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { User } from '../models/User';
import { switchMap, map, filter } from 'rxjs/operators';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { AngularFireDatabase } from '@angular/fire/database';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  currentUser: User = null;

  // Current user observable -> can subscribable for real time updates
  user: Observable<User>;
  private currentUserSource: BehaviorSubject<User> = new BehaviorSubject(null);

  private isLoggedIn: boolean = false;
  private listeners: { login: any[], logout: any[] };

  constructor(
    private afAuth: AngularFireAuth,
    private afdb: AngularFireDatabase,
    private http: HttpClient
  ) {
    afAuth.auth.setPersistence(firebase.auth.Auth.Persistence.SESSION);
    this.user = this.currentUserSource.asObservable().pipe(filter(users => !!users));

    this.listeners = { login: [], logout: [] };

    this.afAuth.authState.pipe(
      switchMap(auth => {
        if (auth) {
          return this.afAuth.idTokenResult;
        } else {
          return of(null);
        }
      }),
      map(decodedToken => {
        if (decodedToken) {
          const user = {
            id: decodedToken.claims.uid,
            token: decodedToken.token,
            name: decodedToken.claims.displayName,
            expire: decodedToken.claims.expire
          }
          if (!this.isLoggedIn) {
            this.isLoggedIn = true;
            this.listeners.login.forEach(callback => {
              callback(user);
            });
          }
          return user;
        } else {
          this.isLoggedIn = false;
          return null;
        }
      })
    ).subscribe(user => {
      this.currentUser = user;
      this.currentUserSource.next(user);
    });
  }

  on( event: "login" | "logout", callback) {
    this.listeners[event].push(callback);
  }

  off(event: "login" | "logout", callback?) {
    if (callback == undefined) {
      this.listeners[event] = [];
    } else {
      const index = this.listeners[event].indexOf(callback);
      this.listeners[event].splice(index, 1);
    }
  }

  getAuth(): Observable<firebase.User> {
    return this.afAuth.authState.pipe( map(auth => auth) );
  }

  async login(email: string, password: string): Promise<any> {
    const firebase_result = await (this.http.post<any>(environment.firebaseLogin.signInWithPassword, { email: email, password: password, returnSecureToken: true }).toPromise()
    .catch(resp => {
      if (resp.error.error.message === 'INVALID_PASSWORD' || resp.error.error.message === 'EMAIL_NOT_FOUND') {
        throw new HttpErrorResponse({error: 'wrong-username-password'});
      } else if (resp.error.error.message.includes('TOO_MANY_ATTEMPTS_TRY_LATER')) {
        throw new HttpErrorResponse({error: 'too-many-attempts'});
      }
      throw resp;
    }));
    const vsight_result = await this.http.post<any>(environment.endPoints.consolelogin, { token: firebase_result.idToken }).toPromise();
    return { firebase_result: firebase_result, vsight_result: vsight_result };
  }

  verifyLoginCode(token: string, code: string) {
    return this.http.post<any>(environment.endPoints.consolelogin, { token: token, verification: code }).toPromise();
  }

  loginToFirebase(token: string) {
    return this.afAuth.auth.signInWithCustomToken(token).then(userCredential => userCredential.user);
  }

  logout() {
    return this.afAuth.auth.signOut().then(() => {
      this.listeners.logout.forEach(callback => {
        callback();
      });
      return;
    });
  }

  getServerTimeOffset(): Promise<number> {
    return this.afdb.database.ref(".info/serverTimeOffset").once("value").then(snap => snap.val());
  }
}
